import { useStore } from '@nanostores/react';
import { useCallback, useState } from 'react';
import { Alert } from '~/components/ui/Alert';
import { Button } from '~/components/ui/Button';
import { Link } from '~/components/ui/Link';
import { purchaseDomainForProject } from '~/lib/.client/api/domains';
import { importStore, type DeploymentInfo } from '~/lib/projects';
import { domainsStore } from '~/lib/stores/domains';
import { projectStore } from '~/lib/stores/project';
import { classNames } from '~/utils/classNames';
import { invariant } from '~/utils/invariant';
import { preventUserInteractions } from '~/utils/overlay';
import { withSpinner } from '~/utils/withSpinner';

interface ConfirmCustomDomainProps {
  className?: string;
}

const broadcastChannel = new BroadcastChannel('domain-purchase-status');

export const ConfirmCustomDomain = ({ className }: ConfirmCustomDomainProps) => {
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [purchaseError, setPurchaseError] = useState<{ message: string; code?: string }>();
  const [paymentUrl, setPaymentUrl] = useState<string | undefined>();

  const domain = useStore(domainsStore.domainPurchaseSelected);

  if (!domain) {
    return null;
  }

  const onCancel = useCallback(() => {
    domainsStore.setBuyNewDomainStep();
  }, []);

  const updateDeploymentInfo = (newInfo: Partial<DeploymentInfo>) => {
    const deploymentInfo = importStore.get().deploymentInfo;

    if (deploymentInfo) {
      // update the deployment info with the new site URL so that the publish menu reflects the new domain
      importStore.setKey('deploymentInfo', {
        ...deploymentInfo,
        ...newInfo,
      });
    }
  };

  const onPurchase = useCallback(() => {
    withSpinner({
      action: async () => {
        const reenable = preventUserInteractions();

        try {
          const projectId = projectStore.projectId.get();
          const organization = projectStore.organization.get();

          invariant(projectId);

          const force = purchaseError?.code === 'price_changed';

          const result = await purchaseDomainForProject(projectId, organization, {
            force,
            domain: domain.domain,
            firstYearPrice: domain.price,
            price: domain.renewalPrice,
          });

          if (result.status === 'payment_required') {
            const paymentWindow = window.open(result.paymentUrl, '_blank');

            if (paymentWindow) {
              setIsConfirming(true);
            }

            setPaymentUrl(result.paymentUrl);

            const timer = setTimeout(() => {
              // re-enable the button after 30 seconds so users can try again
              setIsConfirming(false);
            }, 30_000);

            broadcastChannel.addEventListener('message', (event) => {
              if (typeof event.data === 'object' && 'success' in event.data && 'projectId' in event.data) {
                // cancel the timer
                clearTimeout(timer);

                setIsConfirming(false);

                if (event.data.success) {
                  updateDeploymentInfo({
                    site_url: event.data.siteUrl,
                    is_custom_domain: true,
                  });

                  domainsStore.openManageDomains();
                } else {
                  setPurchaseError({
                    message: event.data.error || 'Something went wrong while confirming the domain purchase.',
                  });
                }
              }
            });

            return;
          }

          domainsStore.domainEntries.set(result.entries);

          updateDeploymentInfo({
            site_url: result.siteUrl,
            is_custom_domain: true,
          });

          domainsStore.resetStep();
          domainsStore.setDomainAlert('Your payment has been processed, thank you.');
        } catch (error) {
          setPurchaseError(
            error instanceof Error ? error : { message: 'Something went wrong while purchasing the domain.' },
          );
        } finally {
          reenable();
        }
      },
      onStateUpdate: setIsPurchasing,
    });
  }, [domain, purchaseError]);

  return (
    <div className={classNames('flex flex-col justify-center gap-4', className)}>
      <h2 className="text-lg font-semibold text-bolt-ds-textPrimary">Confirm your purchase</h2>

      {purchaseError && (
        <Alert type="danger" className="max-w-150">
          {purchaseError.message}
        </Alert>
      )}

      {paymentUrl && (
        <Alert type="warning" className="max-w-150">
          {isConfirming ? (
            <>
              You can now complete your payment. If you did not see the payment window, you can{' '}
              <Link href={paymentUrl} target="_blank">
                click here
              </Link>
              .
            </>
          ) : (
            <>
              In order to complete the purchase, you will have to take an extra confirmation step in a separate window.{' '}
              <Link href={paymentUrl} target="_blank">
                Click here
              </Link>{' '}
              to open the payment page.
            </>
          )}
        </Alert>
      )}

      <p className="text-bolt-elements-textSecondary max-w-112 text-center">
        You're about to register <strong className="text-bolt-ds-textPrimary font-600">{domain.domain}</strong> for{' '}
        <strong className="text-bolt-ds-textPrimary font-600">${domain.price}</strong> (first year). After that, it
        renews automatically at{' '}
        <strong className="text-bolt-ds-textPrimary font-600">${domain.renewalPrice}/year</strong>.
      </p>

      <p className="text-bolt-elements-textSecondary">This purchase is final and non-refundable.</p>

      <Link
        href="https://www.icann.org/resources/pages/benefits-2013-09-16-en"
        target="_blank"
        className="text-sm decoration-none mt-3"
      >
        Learn about your rights as a domain owner
      </Link>

      <div className="flex gap-4 justify-center w-full mt-2">
        <Button variant="secondary" size="lg" disabled={isPurchasing || isConfirming} onClick={onCancel}>
          Cancel
        </Button>

        <Button
          size="lg"
          color={purchaseError?.code === 'price_changed' ? 'danger' : undefined}
          loading={isPurchasing || isConfirming}
          onClick={onPurchase}
        >
          {purchaseError?.code === 'price_changed' ? 'Purchase Anyway' : 'Purchase Domain'}
        </Button>
      </div>
    </div>
  );
};
